import format from 'date-fns/format';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import { addDays as addDaysFns } from 'date-fns/addDays';
import { addMinutes as addMinutesFns } from 'date-fns/addMinutes';
import isDate from 'date-fns/isDate';
import isValid from 'date-fns/isValid';
import startOfDay from 'date-fns/startOfDay';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInMonths from 'date-fns/differenceInMonths';
import differenceInYears from 'date-fns/differenceInYears';
import endOfMonth from 'date-fns/endOfMonth';
import setHours from 'date-fns/setHours';
import setMinutes from 'date-fns/setMinutes';
import getHours from 'date-fns/getHours';
import getMinutes from 'date-fns/getMinutes';
import getDate from 'date-fns/getDate';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import isEqual from 'date-fns/isEqual';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import { max as maxDate } from 'date-fns/max'; // named export because max is a reserved word
import { min as minDate } from 'date-fns/min';

// ============================================================================
// KONSTANTEN
// ============================================================================

const LOCAL_DATE_TIME_OPTIONS = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const LOCAL_SHORT_DATE_TIME_OPTIONS = {
  year: '2-digit',
  month: '2-digit',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

const LOCAL_DATE_WEEKDAY_OPTIONS = {
  year: '2-digit',
  month: 'short',
  day: 'numeric',
  weekday: 'short',
};

const DAY_IN_MINUTES = 24 * 60;
const MINUTES_BEFORE_BOUNDARY = 60;
const ONE_HOUR_BEFORE_BOUNDARY = 70;
const HOURS_BEFORE_BOUNDARY = 22 * 60;
const ONE_DAY_BEFORE_BOUNDARY = 28 * 60;
const DAYS_BEFORE_BOUNDARY = 27 * DAY_IN_MINUTES;
const ONE_MONTH_BEFORE_BOUNDARY = 45 * DAY_IN_MINUTES;
const MONTHS_BEFORE_BOUNDARY = 330 * DAY_IN_MINUTES;
const ONE_YEAR_BEFORE_BOUNDARY = 380 * DAY_IN_MINUTES;

// ============================================================================
// DATUM MANIPULATIONS FUNKTIONEN
// ============================================================================

/**
 * Get a new Date object set to the last day of the desired month
 * @param year
 * @param month - zero based
 * @returns {Date}
 */
function getLastDayOfMonth(year, month) {
  const date = new Date(year, month);
  const lastDay = endOfMonth(date);
  return startOfDay(lastDay);
}

function selectNearestDate(listOfDateIsoStrings, referenceDateAsTimeInMs) {
  let selectedIndex = listOfDateIsoStrings.findIndex((dateIsoString) => parseISO(dateIsoString).getTime() >= referenceDateAsTimeInMs);
  console.debug('selectNearestDate', selectedIndex);
  return {
    selectedIndex,
    selectedDateAsIsoString: listOfDateIsoStrings[selectedIndex],
  };
}

/**
 * Fügt Minuten zu einem Datum hinzu
 * @param {Date} date
 * @param {number} minutes
 * @returns {Date}
 */
function addMinutes(date, minutes) {
  return addMinutesFns(date, minutes);
}

/**
 * Fügt Tage zu einem Datum hinzu
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
function addDays(date, days) {
  return addDaysFns(date, days);
}

// ============================================================================
// PARSING FUNKTIONEN
// ============================================================================

/**
 * Parst einen ISO String zu Millisekunden seit Epoch
 */
function parseISOStringToTime(isoDateString) {
  try {
    return parseISO(isoDateString).getTime();
  } catch (err) {
    console.error('Failed to parseISOStringToTime ', isoDateString, err);
  }
}

/**
 * Parst einen ISO String zu einem Date-Objekt
 */
const parseISOString = (isoDateString) => {
  if (!isoDateString) {
    return null;
  }

  const parsedTime = Date.parse(isoDateString);
  if (!isNaN(parsedTime)) {
    const date = new Date(parsedTime);
    if (isValid(date)) {
      return date;
    }
  }

  try {
    const date = parseISO(isoDateString);
    return isValid(date) ? date : null;
  } catch (err) {
    console.error('Failed to parse ', isoDateString, err);
    return null;
  }
};

/**
 * Parst einen String in Format YYYY-MM-DD
 */
function parseYYYYMMDD(YYYYMMDD) {
  if (!YYYYMMDD) return null;

  // 2025-07-07
  if (YYYYMMDD.length == 10 && YYYYMMDD.includes('-')) {
    return new Date(Number(YYYYMMDD.substring(0, 4)), Number(YYYYMMDD.substring(5, 7)) - 1, Number(YYYYMMDD.substring(8, 10)));
  }

  // 2025-07-07 00:00:00
  if (YYYYMMDD.length == 19 && YYYYMMDD.includes(' ')) {
    return new Date(Number(YYYYMMDD.substring(0, 4)), Number(YYYYMMDD.substring(5, 7)) - 1, Number(YYYYMMDD.substring(8, 10)));
  }

  const dateString = YYYYMMDD.includes('T') ? YYYYMMDD.split('T')[0] : YYYYMMDD;
  const date = parse(dateString, 'yyyy-MM-dd', new Date());

  // Validierung, aber aussagekräftiger
  if (!isValid(date)) {
    console.warn(`Invalid date: ${YYYYMMDD}`);
    return null;
  }

  return startOfDay(date);
}

/**
 * Parst einen String in Format MM-DD-YYYY
 */
function parseMMDDYYYY(MMDDYYYY) {
  if (!MMDDYYYY) return null;

  const year = MMDDYYYY.substring(6, 10);
  const month = MMDDYYYY.substring(3, 5);
  const day = MMDDYYYY.substring(0, 2);

  const date = parse(MMDDYYYY, 'MMddyyyy', new Date());

  if (!isValid(date)) {
    console.warn(`Invalid date: ${MMDDYYYY}`);
    return null;
  }

  return startOfDay(date);
}

// ============================================================================
// FORMATIERUNGS FUNKTIONEN (OHNE Zeitzonenkorrektur!)
// ============================================================================

/**
 * Formatiert ein Date als YYYY-MM-DD
 */
function formatDateAsYYYYMMDD(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd');
}

/**
 * Formatiert ein Date als YYYY-MM-DD HH:mm:ss
 */
function formatDateAsYYYYMMDDHHIISS(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

/**
 * Formatiert ein Date als DD.MM.YYYY HH:mm
 */
function formatDateAsDDMMYYYYHHmm(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'dd.MM.yyyy HH:mm');
}

/**
 * Formatiert ein Date-String als DD.MM.YYYY HH:mm
 */
function formatDateStringAsDDMMYYYYHHmm(dateTimeString) {
  const date = parseISOString(dateTimeString);
  return date ? formatDateAsDDMMYYYYHHmm(date) : '';
}

/**
 * Formatiert Zeit als HH:mm
 */
function formatTimeHHmm(date) {
  if (!date || !isValid(date)) return '';
  return format(date, 'HH:mm');
}

// ============================================================================
// REFORMAT FUNKTIONEN (ISO String -> Format)
// ============================================================================

/**
 * Reformatiert ISO String als YYYY-MM-DD
 */
function reformatDateIsoStringAsYYYYMMDD(dateIsoString, withDash = true) {
  try {
    const date = parseISO(dateIsoString);
    if (!isValid(date)) return '';
    if (withDash) {
      return format(date, 'yyyy-MM-dd');
    } else {
      return format(date, 'dd.MM.yyyy');
    }
  } catch (err) {
    console.warn('reformatDateIsoStringAsYYYYMMDD failed with error ' + err.message);
    return '';
  }
}

/**
 * Reformatiert ISO String als DD.MM.YYYY HH:mm
 */
function reformatDateIsoStringAsYYYYMMDDHHmm(dateIsoString) {
  const date = parseISO(dateIsoString);
  if (!isValid(date)) return '';
  return format(date, 'dd.MM.yyyy HH:mm');
}

/**
 * Reformatiert ISO String als HH:mm
 */
function reformatDateIsoStringAsHHmm(dateIsoString) {
  const date = parseISO(dateIsoString);
  if (!isValid(date)) return '';
  return format(date, 'HH:mm');
}

/**
 * Reformatiert ISO String als HH:mm DD.MM.YYYY
 */
function reformatDateIsoStringAsHHmmDDMMYYYY(dateIsoString) {
  const date = parseISO(dateIsoString);
  if (!isValid(date)) return '';
  return format(date, 'HH:mm dd.MM.yyyy');
}

/**
 * Reformatiert YYYY-MM-DD als ISO String
 */
function reformatYYYYMMDDAsDateIsoString(YYYYMMDD) {
  const date = parseYYYYMMDD(YYYYMMDD);
  return date ? date.toISOString() : null;
}

// ============================================================================
// LOCALE FORMATIERUNGS FUNKTIONEN
// ============================================================================

function formatAsLocaleShortDate(date, locale = 'de-CH') {
  const options = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatAsLocaleLongYearDate(date, locale = 'de-CH') {
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatAsLocaleDayMonth(date, locale = 'de-CH') {
  const options = { month: '2-digit', day: '2-digit' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatAsLocaleNamedShortDate(date, locale = 'de-CH') {
  const options = { month: 'short', day: 'numeric', weekday: 'short' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatAsLocaleWeekDayDate(date, locale = 'de-CH') {
  return date && date instanceof Date ? date.toLocaleDateString(locale, LOCAL_DATE_WEEKDAY_OPTIONS) : date + ' not a date';
}

function formatAsLocaleNamedShortDateWithYear(date, locale = 'de-CH') {
  const options = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatWithMonthNameAndShortWeekday(date, locale = 'de-CH') {
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
  };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatWithShortWeekdayMonthNumber(date, locale = 'de-CH') {
  const options = { month: 'numeric', day: 'numeric', weekday: 'short' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatAsLocaleDayAndMonth(date, locale = 'de-CH') {
  const options = { month: 'numeric', day: 'numeric' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

function formatAsLocaleLongDate(date, locale = 'de-CH') {
  const options = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : '';
}

function formatAsLocaleYearAndMonth(date, locale = 'de-CH') {
  const options = { year: 'numeric', month: 'long' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : '';
}

function formatAsLocaleDateTime(date, locale = 'de-CH') {
  return date.toLocaleTimeString(locale, LOCAL_DATE_TIME_OPTIONS);
}

function formatAsLocaleShortDateTime(input, locale = 'de-CH') {
  const date = isDate(input) ? input : parseISO(input);
  if (isDate(date)) {
    return date.toLocaleTimeString(locale, LOCAL_SHORT_DATE_TIME_OPTIONS);
  } else {
    return '-';
  }
}

function formatDateFromIsoString(isoString, locale = 'de-CH') {
  const date = parseISO(isoString);
  if (!isValid(date)) return '';

  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return date.toLocaleDateString(locale, options);
}

/**
 * Formatiert Zeit aus ISO String oder Date-Objekt als HH:mm
 * @param {string|Date} input - ISO String oder Date-Objekt
 * @param {string} locale - Locale (Standard: 'de-CH')
 * @returns {string} Formatierte Zeit oder leerer String bei Fehler
 */
function formatTime(input, locale = 'de-CH') {
  if (!input) return '';

  // Akzeptiere sowohl Date-Objekte als auch ISO Strings
  const date = isDate(input) ? input : parseISO(input);

  if (!isValid(date)) return '';

  const options = { hour: '2-digit', minute: '2-digit' };
  return date.toLocaleTimeString(locale, options);
}

function formatDateTime(input, locale = 'de-CH') {
  if (!input) return '00:00';

  // Akzeptiert BEIDE: Date-Objekte UND ISO Strings
  const date = isDate(input) ? input : parseISO(input);

  if (!isValid(date)) return '00:00';

  const options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return date.toLocaleDateString(locale, options);
}

function formatWithDateNoYear(date, locale = 'de-CH') {
  const options = { month: 'short', day: 'numeric' };
  return date && date instanceof Date ? date.toLocaleDateString(locale, options) : date + ' not a date';
}

// ============================================================================
// VERGLEICHS FUNKTIONEN
// ============================================================================

function isSameDateIsoString(d1IsoString, d2IsoString) {
  const d1 = parseISOString(d1IsoString);
  const d2 = parseISOString(d2IsoString);
  return isSameDate(d1, d2);
}

function isSameDate(d1, d2) {
  if (!d1 || !d2 || !isValid(d1) || !isValid(d2)) return false;

  return getYear(d1) === getYear(d2) && getMonth(d1) === getMonth(d2) && getDate(d1) === getDate(d2);
}

function compareISOStringDates(a, b) {
  let dateA, dateB;
  try {
    dateA = parseISOString(a);
  } catch (err) {
    console.error(err);
    return -1;
  }
  if (!dateA || !isValid(dateA)) return -1;

  try {
    dateB = parseISOString(b);
  } catch (err) {
    return 1;
  }
  if (!dateB || !isValid(dateB)) return 1;

  const timeA = dateA.getTime();
  const timeB = dateB.getTime();
  const diff = timeA - timeB;

  if (diff === 0) return 0;
  return diff < 0 ? -1 : 1;
}

/**
 * Gibt das neueste Datum aus einem Array von ISO Strings zurück
 */
function maxFromIsoStrings(isoDateStrings) {
  if (!isoDateStrings || isoDateStrings.length === 0) return null;

  const dates = isoDateStrings.map((iso) => parseISOString(iso)).filter((d) => d && isValid(d));

  return dates.length > 0 ? maxDate(dates) : null;
}

/**
 * Gibt das älteste Datum aus einem Array von ISO Strings zurück
 */
function minFromIsoStrings(isoDateStrings) {
  if (!isoDateStrings || isoDateStrings.length === 0) return null;

  const dates = isoDateStrings.map((iso) => parseISOString(iso)).filter((d) => d && isValid(d));

  return dates.length > 0 ? minDate(dates) : null;
}

// ============================================================================
// ZEIT FUNKTIONEN
// ============================================================================

export function dateHHMMToDate(date, timeString) {
  let date_ = date || new Date();
  let timeString_ = timeString || '00:00';

  const hhmm = timeString_.includes(':') ? timeString_.split(':') : timeString_.includes('.') ? timeString_.split('.') : ['8', '0'];

  let dateObj = typeof date_.getMonth !== 'function' ? parseISO(date_) : date_;

  const hours = Number(hhmm[0]);
  const minutes = Number(hhmm[1]);

  let result = setHours(dateObj, hours);
  result = setMinutes(result, minutes);

  return result;
}

export function parseTimeString(date, timeString) {
  const result = dateHHMMToDate(date, timeString);
  return result.toISOString();
}

export function getMinutesAfterMidnightFromTimeIsoString(timeIsoString = '') {
  const date = parseISO(timeIsoString);

  if (!isValid(date)) {
    console.debug(`timeIsoString=${timeIsoString} return 0`);
    return 0;
  }

  const hh = getHours(date);
  const mm = getMinutes(date);

  return hh * 60 + mm;
}

export function getTimeFromMinutesAfterMidnight(timeFormat, minutesAfterMidnight, dateString = '') {
  dateString = dateString === '' ? format(new Date(), 'yyyy-MM-dd') : dateString;

  let d = parseISO(dateString);
  d = setHours(d, 0);
  d = setMinutes(d, minutesAfterMidnight);

  let hoursOfDay = getHours(d);

  if (minutesAfterMidnight >= 24 * 60 && timeFormat === '24+hh:mm') {
    hoursOfDay += 24;
  }

  const hours = hoursOfDay < 10 ? '0' + hoursOfDay : hoursOfDay;
  const minutes = getMinutes(d) < 10 ? '0' + getMinutes(d) : getMinutes(d);

  return `${hours}:${minutes}`;
}

// ============================================================================
// RELATIVE ZEIT FUNKTIONEN
// ============================================================================

function getBeforeText(dateIsoString) {
  const dateupdated = parseISO(dateIsoString);
  const now = new Date();

  const minutesDifference = differenceInMinutes(now, dateupdated);
  const days = differenceInDays(now, dateupdated);
  const months = differenceInMonths(now, dateupdated);
  const years = differenceInYears(now, dateupdated);

  if (minutesDifference < MINUTES_BEFORE_BOUNDARY) {
    return { key: 'minutesBefore', minutes: minutesDifference };
  }
  if (minutesDifference < ONE_HOUR_BEFORE_BOUNDARY) {
    return { key: 'oneHourBefore' };
  }
  if (minutesDifference < HOURS_BEFORE_BOUNDARY) {
    return { key: 'hoursBefore', hours: Math.round(minutesDifference / 60) };
  }
  if (minutesDifference < ONE_DAY_BEFORE_BOUNDARY) {
    return { key: 'oneDayBefore' };
  }
  if (minutesDifference < DAYS_BEFORE_BOUNDARY) {
    return { key: 'daysBefore', days: Math.round(days) };
  }
  if (minutesDifference < ONE_MONTH_BEFORE_BOUNDARY) {
    return { key: 'oneMonthBefore' };
  }
  if (minutesDifference < MONTHS_BEFORE_BOUNDARY) {
    return { key: 'monthsBefore', months: Math.max(2, Math.round(months)) };
  }
  if (minutesDifference < ONE_YEAR_BEFORE_BOUNDARY) {
    return { key: 'oneYearBefore' };
  }

  const monthReminder = months - years * 12;

  if (years > 1) {
    if (monthReminder >= 2) {
      return { key: 'yearsAndMonthBefore', years, months: monthReminder };
    } else {
      return { key: 'yearsBefore', years };
    }
  }

  return { key: 'oneYearAndMonthBefore', months: monthReminder };
}

// ============================================================================
// VALIDIERUNGS FUNKTIONEN
// ============================================================================

function isValidYYYYMMDD(date) {
  const regex = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
  const match = date.match(regex);

  if (!match) return false;

  const year = parseInt(match[1]);
  const month = parseInt(match[2]);
  const day = parseInt(match[3]);

  // Check for February and leap year
  if (month === 2) {
    if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
      return day <= 29;
    }
    return day <= 28;
  }

  // Check for months with only 30 days
  if ([4, 6, 9, 11].includes(month)) {
    return day <= 30;
  }
  return true;
}

// ============================================================================
// KOMPLEXE FUNKTIONEN
// ============================================================================

function isBetweenIncludingEndDate(selectedDate, displayFromDateString, includingDisplayToDateString, offsetHours) {
  const selectedDateTime = selectedDate.getTime();
  if (!displayFromDateString && !includingDisplayToDateString) return true;

  const displayFrom = displayFromDateString ? parseYYYYMMDD(displayFromDateString) : null;
  const displayTo = includingDisplayToDateString ? parseYYYYMMDD(includingDisplayToDateString) : null;

  const beginnOk = displayFrom ? displayFrom.getTime() <= selectedDateTime : true;
  const endOk = displayTo ? displayTo.getTime() >= selectedDateTime : true;

  if (beginnOk && endOk) {
    return true;
  }

  const oneDayAfter = addDaysFns(displayTo, 1);
  const now = new Date();

  const isSameDate = format(now, 'yyyy-MM-dd') === format(oneDayAfter, 'yyyy-MM-dd');

  const currentTimeInSeconds = getHours(now) * 3600 + getMinutes(now) * 60 + now.getSeconds();
  const offsetSeconds = offsetHours * 3600;
  const isTimeOKay = offsetSeconds >= currentTimeInSeconds;

  return isSameDate && isTimeOKay;
}

// ============================================================================
// EXPORT
// ============================================================================

export default {
  // Datum Manipulation
  getLastDayOfMonth,
  selectNearestDate,
  addMinutes,
  addDays,

  // Parsing
  parseISOString,
  parseISOStringToTime,
  parseYYYYMMDD,
  parseMMDDYYYY,
  parseTimeString,
  dateHHMMToDate,

  // Formatierung
  formatDateAsYYYYMMDD,
  formatDateAsYYYYMMDDHHIISS,
  formatDateTime,
  formatTime,
  formatTimeHHmm,
  formatDateStringAsDDMMYYYYHHmm,

  // Reformat
  reformatDateIsoStringAsYYYYMMDD,
  reformatDateIsoStringAsYYYYMMDDHHmm,
  reformatDateIsoStringAsHHmm,
  reformatDateIsoStringAsHHmmDDMMYYYY,
  reformatYYYYMMDDAsDateIsoString,

  // Locale Formatierung
  formatAsLocaleShortDate,
  formatAsLocaleLongYearDate,
  formatAsLocaleDayMonth,
  formatAsLocaleNamedShortDate,
  formatAsLocaleWeekDayDate,
  formatAsLocaleNamedShortDateWithYear,
  formatWithMonthNameAndShortWeekday,
  formatWithShortWeekdayMonthNumber,
  formatAsLocaleDayAndMonth,
  formatAsLocaleLongDate,
  formatAsLocaleYearAndMonth,
  formatAsLocaleDateTime,
  formatAsLocaleShortDateTime,
  formatWithDateNoYear,
  formatDateFromIsoString,

  // Zeit Funktionen
  getMinutesAfterMidnightFromTimeIsoString,
  getTimeFromMinutesAfterMidnight,

  // Vergleich
  isSameDate,
  isSameDateIsoString,
  compareISOStringDates,
  maxFromIsoStrings,
  minFromIsoStrings,

  // Validierung
  isValidYYYYMMDD,

  // Relative Zeit
  getBeforeText,

  // Komplex
  isBetweenIncludingEndDate,
};
